import { useCallback, useEffect, useState } from "react";
import { track } from "../analytics/tracker";
import { clientAuthApi, type ClientSession } from "../api/auth";

const TOKEN_KEY = "client_token";
const EVENT = "client-auth-changed";

function readToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function emitChanged() {
  window.dispatchEvent(new Event(EVENT));
}

export function setClientToken(token?: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  emitChanged();
}

export function notifyClientSessionChanged() {
  emitChanged();
}

const guest: ClientSession = { plan: "guest", planName: "游客" };

export function useClientSession() {
  const [session, setSession] = useState<ClientSession>(guest);
  const [loading, setLoading] = useState(Boolean(readToken()));

  const reload = useCallback(async (silent = false) => {
    if (!readToken()) {
      setSession(guest);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      setSession(await clientAuthApi.me());
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "";
      if (/未登录|无效登录|Unauthorized|jwt expired|invalid token/i.test(message)) {
        setClientToken(null);
        setSession(guest);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const onChanged = () => {
      void reload(true);
    };
    window.addEventListener(EVENT, onChanged);
    return () => window.removeEventListener(EVENT, onChanged);
  }, [reload]);

  const login = useCallback(async (phone: string, password: string) => {
    const result = await clientAuthApi.login(phone, password);
    setClientToken(result.token);
    setSession(result);
    track("auth", "login", "登录成功", { userId: result.id, plan: result.plan });
    return result;
  }, []);

  const register = useCallback(async (phone: string, password: string, code: string) => {
    const result = await clientAuthApi.register(phone, password, code);
    setClientToken(result.token);
    setSession(result);
    track("auth", "register", "注册成功", { userId: result.id, plan: result.plan });
    return result;
  }, []);

  const sendSms = useCallback((phone: string, scene: "register" | "login") => {
    return clientAuthApi.sendSms(phone, scene);
  }, []);

  const logout = useCallback(() => {
    track("auth", "logout", "退出登录");
    setClientToken(null);
    setSession(guest);
  }, []);

  return { session, loading, login, register, sendSms, logout, reload };
}
